<svelte:window on:popstate={popState} />

<Tabs {tabs} bind:selectedIndex={selectedTab}>
    <h2 class="screen-reader">{tabs[selectedTab].title}</h2>
    {#if selectedTab !== plannerTabIds.topics}
        <div
            class={`its-plans-view-switch ${
                showEmptyState
                    ? "its-plans-view-switch-position-empty"
                    : "its-plans-view-switch-position-list"
            }`}
        >
            {#if !showEmptyState}
                <PlansToolbar
                    data={tabData}
                    selectedView={getPlannerView(selectedTab)}
                    {selectedPlanIds}
                    on:endOfActionOnSelectedPlans={afterAction}
                />
            {/if}
            <TableViewLink
                {config}
                {i18n}
                isDataLoading={tabData.loading}
                backExtraParams={{
                    selectedTab
                }}
            />
        </div>
    {/if}

    <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
    <section
        id="panel-0"
        aria-labelledby="tab-current"
        role="tabpanel"
        class="{contentClass} its-plans__current"
        tabindex="-1"
    >
        {#if showEmptyState}
            <EmptyState
                {iconName}
                title={titleEmptyTab}
                description={i18n.emptyState_CurrentTab_Description}
                showLink={config.canEdit}
                linkType="primary-standalone"
                linkText={i18n.addPlan}
                href={createNewPlanUrl}
                on:click={createNewPlan}
            />
            {#if config.canEdit && config.isImportEnabled}
                <UseReadyMadeContentLink
                    href={`/Courses/${
                        config.courseId
                    }/ImportContent?importTopic=false&fromOverview=false&returnUrl=${encodeURI(window.location.origin + window.location.pathname)}`}
                    linktext={i18n.useReadyMadeContent}
                    emptyState={true}
                ></UseReadyMadeContentLink>
            {/if}
        {:else}
            <PlansView
                separateUpcomingPlans
                data={$currentPlansData}
                loadMethod={loadCurrentPlans}
                {selectedPlanIds}
                {columnVisibilities}
                {useGenericResourceColumn}
                on:endOfActionOnSelectedPlans={afterAction}
            />
        {/if}
    </section>
    <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
    <section
        id="panel-1"
        aria-labelledby="tab-past"
        role="tabpanel"
        class={contentClass}
        tabindex="-1"
    >
        {#if showEmptyState}
            <EmptyState
                {iconName}
                title={titleEmptyTab}
                description={i18n.emptyState_PastTab_Description}
                showLink={config.canEdit}
                linkType="primary-standalone"
                linkText={i18n.addPlan}
                href={createNewPlanUrl}
                on:click={createNewPlan}
            />
            {#if config.canEdit && config.isImportEnabled}
                <UseReadyMadeContentLink
                    href={`/Courses/${
                        config.courseId
                    }/ImportContent?importTopic=false&fromOverview=false&returnUrl=${encodeURI(window.location.origin + window.location.pathname)}`}
                    linktext={i18n.useReadyMadeContent}
                    emptyState={true}
                ></UseReadyMadeContentLink>
            {/if}
        {:else}
            <PlansView
                data={$pastPlansData}
                loadMethod={loadPastPlans}
                {selectedPlanIds}
                {columnVisibilities}
                {useGenericResourceColumn}
                on:endOfActionOnSelectedPlans={afterAction}
            />
        {/if}
    </section>
    <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
    <section
        id="panel-2"
        aria-labelledby="tab-withoutdate"
        role="tabpanel"
        class={contentClass}
        tabindex="-1"
    >
        {#if showEmptyState}
            <EmptyState
                {iconName}
                title={titleEmptyTab}
                description={i18n.emptyState_WithoutDateTab_Description}
                showLink={config.canEdit}
                linkType="primary-standalone"
                linkText={i18n.addPlan}
                href={createNewPlanUrl}
                on:click={createNewPlan}
            />
            {#if config.canEdit && config.isImportEnabled}
                <UseReadyMadeContentLink
                    href={`/Courses/${
                        config.courseId
                    }/ImportContent?importTopic=false&fromOverview=false&returnUrl=${encodeURI(window.location.origin + window.location.pathname)}`}
                    linktext={i18n.useReadyMadeContent}
                    emptyState={true}
                ></UseReadyMadeContentLink>
            {/if}
        {:else}
            <PlansView
                data={$withoutDatePlansData}
                loadMethod={loadWithoutDatePlans}
                {selectedPlanIds}
                {columnVisibilities}
                {useGenericResourceColumn}
                on:endOfActionOnSelectedPlans={afterAction}
            />
        {/if}
    </section>
    <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
    <section
        id="panel-3"
        aria-labelledby="tab-topics"
        role="tabpanel"
        class={contentClass}
        tabindex="-1"
    >
        <Topics
            data={$topicsData}
            {selectedTab}
            on:endOfActionOnSelectedTopics={afterAction}
            on:createTopic={afterAction}
        />
    </section>
</Tabs>

<script>
    import { onMount } from "svelte";
    import { get } from "svelte/store";

    import EmptyState from "@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte";
    import Tabs from "@itslearning/prometheus/assets/navigation/Tabs/v1/Tabs.svelte";

    import PlansView from "@its-ui/planner-common/components/PlansView/PlansView.svelte";
    import TableViewLink from "@its-ui/planner-common/components/TableViewLink/TableViewLink.svelte";
    import PlansToolbar from "@its-ui/planner-common/components/PlansToolbar/PlansToolbar.svelte";
    import UseReadyMadeContentLink from "@its-ui/planner-common/components/UseReadyMadeContentLink/UseReadyMadeContentLink.svelte";

    import { addBackDestinationParam } from "@its-ui/planner-common/helpers/backLinkHelper";

    import Topics from "../Topics/Topics.svelte";

    import config from "@its-ui/planner-common/config";
    import i18n from "@its-ui/planner-common/i18n";
    import { createPlan, createPlanV2 } from "@its-ui/planner-common/api";
    import { plannerTabIds } from "@its-ui/planner-common/constants";

    import {
        currentPlansData,
        pastPlansData,
        withoutDatePlansData,
        topicsData,
        selectedPlanIds,
        allPlanCountsData,
        selectedTopicIds
    } from "@its-ui/planner-common/logic/plans/stores";
    import {
        loadCurrentPlans,
        loadPastPlans,
        loadWithoutDatePlans,
        loadTopics,
        loadAllPlanCounts
    } from "@its-ui/planner-common/logic/plans/loadDataController";
    import { getPlannerView } from "@its-ui/planner-common/helpers/converters";

    onMount(() => {
        loadAllPlanCounts(config.publicAccessKey);
    });

    /** Empty state variables **/
    const iconName = "plans";
    const titleEmptyTab = config.canEdit
        ? i18n.emptyState_Tab_Title_Teacher
        : i18n.emptyState_Title_Student;

    let showEmptyState = false;
    let tabData = {};
    let hasCurrentPlansLoaded = false;
    let hasPastPlansLoaded = false;
    let hasWithoutDatePlansLoaded = false;
    let hasTopicsLoaded = false;

    const columnVisibilities = config.columnVisibilities;
    const useGenericResourceColumn = config.useGenericResourceColumn;
    const selectedTabFromHref = getSelectedTabFromHref();

    export let selectedTab = selectedTabFromHref ? selectedTabFromHref : plannerTabIds.current;
    export let allCounts;
    let lastSelectedTab = selectedTab;
    let createNewPlanUrl = "#";

    const contentClass = "prom-tabs__content";
    const maxCountTreshold = 100;
    const maxCountDisplayText = " (99+)";
    const tabs = [
        {
            id: "tab-current",
            route: "current",
            target: "#panel-0",
            title: i18n.currentTab,
            loadMethod: loadCurrentPlans
        },
        {
            id: "tab-past",
            route: "past",
            target: "#panel-1",
            title: i18n.previousTab,
            loadMethod: loadPastPlans
        },
        {
            id: "tab-withoutdate",
            route: "withoutdate",
            target: "#panel-2",
            title: i18n.withoutDateTab,
            loadMethod: loadWithoutDatePlans
        },
        {
            id: "tab-topics",
            route: "topics",
            target: "#panel-3",
            title: config.topicName,
            loadMethod: loadTopics
        }
    ];

    $: {
        allCounts = $allPlanCountsData;

        if (!allCounts.loading) {
            addCountsToTabTitles();
        }
    }

    $: {
        switch (selectedTab) {
            case plannerTabIds.current:
                showEmptyState = !$currentPlansData.loading && $currentPlansData.plans.length === 0;
                tabData = $currentPlansData;
                break;
            case plannerTabIds.past:
                showEmptyState = !$pastPlansData.loading && $pastPlansData.plans.length === 0;
                tabData = $pastPlansData;
                break;
            case plannerTabIds.withoutDate:
                showEmptyState =
                    !$withoutDatePlansData.loading && $withoutDatePlansData.plans.length === 0;
                tabData = $withoutDatePlansData;
                break;
            default:
                break;
        }
    }

    $: {
        switch (selectedTab) {
            case plannerTabIds.current:
                if (!hasCurrentPlansLoaded && tabs[selectedTab].loadMethod) {
                    tabs[selectedTab].loadMethod(config.publicAccessKey);
                    hasCurrentPlansLoaded = true;
                }

                break;
            case plannerTabIds.past:
                if (!hasPastPlansLoaded && tabs[selectedTab].loadMethod) {
                    tabs[selectedTab].loadMethod(config.publicAccessKey);
                    hasPastPlansLoaded = true;
                }

                break;
            case plannerTabIds.withoutDate:
                if (!hasWithoutDatePlansLoaded && tabs[selectedTab].loadMethod) {
                    tabs[selectedTab].loadMethod(config.publicAccessKey);
                    hasWithoutDatePlansLoaded = true;
                }

                break;
            case plannerTabIds.topics:
                if (!hasTopicsLoaded && tabs[selectedTab].loadMethod) {
                    tabs[selectedTab].loadMethod(config.publicAccessKey);
                    hasTopicsLoaded = true;
                }

                break;
        }
    }

    $: if (selectedTab !== lastSelectedTab) {
        const selectedPlans = get(selectedPlanIds);
        const selectedTopics = get(selectedTopicIds);

        if (
            (selectedPlans && selectedPlans.size > 0) ||
            (selectedTopics && selectedTopics.size > 0)
        ) {
            resetLoadStatusOfSelectedTab();
        }

        lastSelectedTab = selectedTab;
        history.pushState(
            { tab: selectedTab },
            "",
            config.publicAccessKey
                ? `/plans/public/${config.publicAccessKey}/${tabs[selectedTab].route}/`
                : `/plans/courses/${config.courseId}/${tabs[selectedTab].route}/`
        );

        /** Clear plan cards and topic cards that are checked in other tabs **/
        selectedTopicIds.set(new Set([]));
        selectedPlanIds.set(new Set([]));
    }

    function addCountsToTabTitles() {
        tabs[0].title =
            allCounts.currentPlansCount >= maxCountTreshold
                ? `${i18n.currentTab} ${maxCountDisplayText}`
                : `${i18n.currentTab} (${allCounts.currentPlansCount})`;
        tabs[1].title =
            allCounts.pastPlansCount >= maxCountTreshold
                ? `${i18n.previousTab} ${maxCountDisplayText}`
                : `${i18n.previousTab} (${allCounts.pastPlansCount})`;
        tabs[2].title =
            allCounts.withoutDatePlansCount >= maxCountTreshold
                ? `${i18n.withoutDateTab} ${maxCountDisplayText}`
                : `${i18n.withoutDateTab} (${allCounts.withoutDatePlansCount})`;
        tabs[3].title =
            allCounts.topicsCount >= maxCountTreshold
                ? `${config.topicName} ${maxCountDisplayText}`
                : `${config.topicName} (${allCounts.topicsCount})`;
    }

    async function createNewPlan(event) {
        event.preventDefault();

        const planParams = {
            topic: null,
            start: "0000-00-00 00:00:00",
            stop: "0000-00-00 00:00:00"
        };

        const newPlan = config.assignToEnabled ? await createPlanV2(planParams) : await createPlan(planParams);

        createNewPlanUrl = addBackDestinationParam(
            `${config.newPlanUrl}/${newPlan.planId}?pendingPlanId=${newPlan.planId}`
        );

        window.location.href = createNewPlanUrl;

        return false;
    }

    async function afterAction() {
        resetLoadStatus();
        await loadAllPlanCounts();

        switch (selectedTab) {
            case plannerTabIds.current:
                await loadCurrentPlans(config.publicAccessKey);
                break;
            case plannerTabIds.past:
                await loadPastPlans(config.publicAccessKey);
                break;
            case plannerTabIds.withoutDate:
                await loadWithoutDatePlans(config.publicAccessKey);
                break;
            case plannerTabIds.topics:
                loadTopics(config.publicAccessKey);
                break;
            default:
                break;
        }
    }

    function popState({ state }) {
        if (state && state.tab !== undefined) {
            selectedTab = state.tab;
            lastSelectedTab = state.tab;
        }
    }

    function resetLoadStatus() {
        hasCurrentPlansLoaded = false;
        hasPastPlansLoaded = false;
        hasWithoutDatePlansLoaded = false;
        hasTopicsLoaded = false;
    }

    function resetLoadStatusOfSelectedTab() {
        switch (lastSelectedTab) {
            case plannerTabIds.current:
                hasCurrentPlansLoaded = false;
                break;
            case plannerTabIds.past:
                hasPastPlansLoaded = false;
                break;
            case plannerTabIds.withoutDate:
                hasWithoutDatePlansLoaded = false;
                break;
            case plannerTabIds.topics:
                hasTopicsLoaded = false;
                break;
            default:
                break;
        }
    }

    function getSelectedTabFromHref() {
        if (window.location.href.indexOf("past") !== -1) {
            return plannerTabIds.past;
        } else if (window.location.href.indexOf("withoutdate") !== -1) {
            return plannerTabIds.withoutDate;
        } else if (window.location.href.indexOf("topics") !== -1) {
            return plannerTabIds.topics;
        }
    }
</script>
